import React, { createContext, useContext, useMemo, useState } from 'react';
import en from '../i18n/en.json';
import es from '../i18n/es.json';

type Locale = 'en' | 'es';

type LocaleContextType = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: any;
};

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(() => {
    try {
      if (typeof window !== 'undefined') {
        const stored = window.localStorage.getItem('aizora_locale');
        if (stored === 'en' || stored === 'es') return stored;
        const nav = (navigator && (navigator.language || 'en')) || 'en';
        return nav.toLowerCase().startsWith('es') ? 'es' : 'en';
      }
    } catch (e) {
      // ignore
    }
    return 'en';
  });

  const setLocale = (l: Locale) => {
    try {
      if (typeof window !== 'undefined') window.localStorage.setItem('aizora_locale', l);
    } catch (e) {
      // ignore
    }
    setLocaleState(l);
  };

  const t = useMemo(() => (locale === 'es' ? es : en), [locale]);

  return <LocaleContext.Provider value={{ locale, setLocale, t }}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
};

export default LocaleContext;
